/**
 * The debt-service-coverage rows of the Economics tab (WP-G).
 *
 *   DSCR (In-Place, Interest-Only, Act/360)
 *   DSCR (Stabilized, Interest-Only, Act/360)
 *   DSCR (Broker-Stated)
 *   DSCR after FF&E (Hotel)            — hotels only
 *
 * Each row carries its value and its BASIS: how the figure was made, from which
 * operands, or — where it is blank — exactly what it is waiting on. A figure the
 * platform computed is never shown as the broker's, and the broker's is never
 * replaced by it: they are separate rows.
 *
 * The server derives and persists the three computed rows; the stored value is
 * what is shown. Where the server has not yet swept the deal the same figure is
 * computed here from the same inputs by the browser's twin of the backend
 * arithmetic (`ioDscr.ts`, pinned to it by a shared fixture), and the basis says
 * the figure follows from the numbers shown.
 */

import { formatAmount, formatMultiple } from './format'
import {
  ACT_360,
  ACT_365,
  DEFAULT_DAY_COUNT,
  dayCountLabel,
  ioCoverage,
  normalizeDayCount,
  parseAllInRate,
  type DayCount,
  type IoCoverage,
} from './ioDscr'

export type CoverageRowId = 'dscr_io' | 'dscr_io_stabilized' | 'dscr_stated' | 'dscr_hotel_after_ffe'

export type CoverageRow = {
  id: CoverageRowId
  label: string
  /** Undefined where the inputs are missing — shown as a dash, never as zero. */
  value: number | undefined
  /** How the figure was made, or what it is waiting on. */
  basis: string
  /** `computed`: the platform's own arithmetic. `stated`: a package or a person. */
  source: 'computed' | 'stated' | null
}

type Getter = (name: string) => unknown

function present(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
}

function positive(value: unknown): number | undefined {
  if (!present(value)) return undefined
  const number = Number(value)
  return number > 0 ? number : undefined
}

const SHORT: Record<DayCount, string> = { 'ACT/360': 'Act/360', '30/360': '30/360', 'ACT/365': 'Act/365' }
const FACTOR_TEXT: Record<DayCount, string> = { 'ACT/360': '365/360', 'ACT/365': '365/365', '30/360': '360/360' }

function rateText(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`
}

/** What a coverage figure was computed from, in one line a reader can check. */
function basisOf(coverage: IoCoverage, principalLabel: string, options: { reserve?: number; noi?: number }): string {
  const cash = options.reserve !== undefined && options.noi !== undefined
    ? `(NOI ${formatAmount(options.noi)} − FF&E reserve ${formatAmount(options.reserve)})`
    : `NOI ${formatAmount(coverage.cashFlow)}`
  const accrual = coverage.dayCount === ACT_360 || coverage.dayCount === ACT_365
    ? ` × ${FACTOR_TEXT[coverage.dayCount]}` : ''
  return (
    `${cash} ÷ interest-only debt service ${formatAmount(coverage.debtService)} `
    + `(${formatAmount(coverage.principal)} ${principalLabel} × ${rateText(coverage.rate)}${accrual}, `
    + `${dayCountLabel(coverage.dayCount)})`
  )
}

export function buildCoverageRows(input: {
  get: Getter
  lodging: boolean
  /** The catalogue fields the platform derived (`derived_fields` on the record). */
  derivedFields?: readonly string[]
}): CoverageRow[] {
  const { get } = input

  const dayCountRaw = get('day_count')
  const dayCountBlank = dayCountRaw === null || dayCountRaw === undefined || String(dayCountRaw).trim() === ''
  const dayCount: DayCount | null = dayCountBlank ? DEFAULT_DAY_COUNT : normalizeDayCount(dayCountRaw)
  const short = SHORT[dayCount ?? DEFAULT_DAY_COUNT]

  const rateRaw = get('rate')
  const rate = parseAllInRate(rateRaw)
  const rateDisplay = present(rateRaw) || (typeof rateRaw === 'string' && rateRaw.trim() !== '')
    ? String(rateRaw).trim()
    : ''

  const loanAmount = positive(get('loan_amount'))
  // A stated zero initial advance is a real answer (a deal funded entirely
  // later), and the loan amount does NOT stand in for it — same as the server.
  const initialStated = present(get('initial_funding'))
  const initial = initialStated ? Number(get('initial_funding')) : undefined
  const inPlacePrincipal = initialStated ? initial : loanAmount
  const inPlaceLabel = initialStated ? 'initial advance' : 'loan amount'
  const total = positive(get('fully_funded_amount'))
  const stabilizedPrincipal = total ?? loanAmount
  const stabilizedLabel = total !== undefined ? 'total commitment' : 'loan amount'

  const noi = present(get('noi')) ? Number(get('noi')) : undefined
  const noiStabilized = present(get('noi_stabilized')) ? Number(get('noi_stabilized')) : undefined
  const reserve = positive(get('reserve'))

  const missing = (items: Array<[boolean, string]>): string => {
    const needs = items.filter(([absent]) => absent).map(([, text]) => text)
    return needs.length ? `Needs ${needs.join('; ')}.` : 'Not computable from the figures on the deal.'
  }
  // A plain number outside the credible band is a mis-read, not a spread; any
  // other wording ("SOFR + 350 bps", a range, a rate with a floor) states a spread
  // or a bound, and no index level is assumed to turn it into a rate.
  const rateIsPlainNumber = rateDisplay !== '' && Number.isFinite(Number(rateDisplay.replace(/[$,%\s]/g, '')))
  const rateNeed = !rateDisplay
    ? 'an all-in interest rate'
    : rateIsPlainNumber
      ? `an all-in interest rate between 1% and 30% (“${rateDisplay}” is outside that, and is not read as a rate)`
      : `an all-in interest rate as one number (“${rateDisplay}” states a spread over an index, not a rate — no index level is assumed)`
  const dayCountNeed = 'a recognized day count (Actual/360, 30/360 or Actual/365)'

  /** One computed row: the stored figure, else the figure these inputs give. */
  const computedRow = (
    id: CoverageRowId,
    label: string,
    stored: unknown,
    coverage: IoCoverage | null,
    principalLabel: string,
    basisOptions: { reserve?: number; noi?: number },
    needs: Array<[boolean, string]>,
  ): CoverageRow => {
    const storedValue = present(stored) && Number(stored) !== 0 ? Number(stored) : undefined
    if (storedValue !== undefined) {
      if (coverage === null) {
        return {
          id, label, value: storedValue, source: 'computed',
          basis: 'Computed by the platform from the deal’s figures; its lineage lists the inputs it used.',
        }
      }
      const agrees = Math.abs(storedValue - coverage.dscr) <= 0.005 * Math.max(Math.abs(coverage.dscr), 1e-9)
      return {
        id, label, value: storedValue, source: 'computed',
        basis: agrees
          ? basisOf(coverage, principalLabel, basisOptions)
          : `Stored ${formatMultiple(storedValue)}; the figures shown give ${formatMultiple(coverage.dscr)} and it recomputes on the next save.`,
      }
    }
    if (coverage !== null) {
      return {
        id, label, value: coverage.dscr, source: 'computed',
        basis: `${basisOf(coverage, principalLabel, basisOptions)} — follows from the figures shown; not yet saved to the deal.`,
      }
    }
    return { id, label, value: undefined, source: null, basis: missing(needs) }
  }

  const code = dayCount ?? undefined
  const inPlace = noi !== undefined && dayCount !== null
    ? ioCoverage(noi, inPlacePrincipal, rateRaw, code)
    : null
  const stabilized = noiStabilized !== undefined && dayCount !== null
    ? ioCoverage(noiStabilized, stabilizedPrincipal, rateRaw, code)
    : null

  const rows: CoverageRow[] = [
    computedRow(
      'dscr_io', `DSCR (In-Place, Interest-Only, ${short})`, get('dscr_io'),
      inPlace, inPlaceLabel, {},
      [
        [noi === undefined, 'in-place NOI'],
        [inPlacePrincipal === undefined || inPlacePrincipal <= 0, 'an initial advance or a loan amount'],
        [rate === null, rateNeed],
        [dayCount === null, dayCountNeed],
      ],
    ),
    computedRow(
      'dscr_io_stabilized', `DSCR (Stabilized, Interest-Only, ${short})`, get('dscr_io_stabilized'),
      stabilized, stabilizedLabel, {},
      [
        [noiStabilized === undefined, 'stabilized NOI'],
        [stabilizedPrincipal === undefined, 'a total commitment or a loan amount'],
        [rate === null, rateNeed],
        [dayCount === null, dayCountNeed],
      ],
    ),
  ]

  // The broker's own figure: kept as stated, and never the platform's.
  const statedStored = present(get('dscr_stated')) && Number(get('dscr_stated')) !== 0 ? Number(get('dscr_stated')) : undefined
  const dscrField = present(get('dscr')) && Number(get('dscr')) !== 0 ? Number(get('dscr')) : undefined
  const dscrIsDerived = (input.derivedFields ?? []).includes('dscr')
  if (statedStored !== undefined) {
    rows.push({
      id: 'dscr_stated', label: 'DSCR (Broker-Stated)', value: statedStored, source: 'stated',
      basis: 'As stated by the package or a person — kept as stated; a computed figure never replaces it.',
    })
  } else if (dscrField !== undefined && !dscrIsDerived) {
    rows.push({
      id: 'dscr_stated', label: 'DSCR (Broker-Stated)', value: dscrField, source: 'stated',
      basis: 'The DSCR this deal states, as recorded; it is carried to this field on the next save.',
    })
  } else {
    rows.push({
      id: 'dscr_stated', label: 'DSCR (Broker-Stated)', value: undefined, source: null,
      basis: dscrField !== undefined
        ? 'No package or person has stated a DSCR; the DSCR on the deal is the platform’s NOI ÷ debt service.'
        : 'No package or person has stated a DSCR on this deal.',
    })
  }

  if (input.lodging) {
    const hotel = noi !== undefined && dayCount !== null && reserve !== undefined
      ? ioCoverage(noi, inPlacePrincipal, rateRaw, code, { reserve, hotelBasis: true })
      : null
    rows.push(computedRow(
      'dscr_hotel_after_ffe', 'DSCR after FF&E (Hotel)', get('dscr_hotel_after_ffe'),
      hotel, inPlaceLabel, { reserve, noi },
      [
        [reserve === undefined, 'the FF&E reserve entered on the deal (none is ever assumed)'],
        [noi === undefined, 'in-place NOI'],
        [inPlacePrincipal === undefined || inPlacePrincipal <= 0, 'an initial advance or a loan amount'],
        [rate === null, rateNeed],
        [dayCount === null, dayCountNeed],
      ],
    ))
  }
  return rows
}
